import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { useCambiosActivos, type ProblemaBajaActivo } from './datos';

// Confirma la baja de un activo (#119), con el molde de `EliminarSucursal`. La
// baja es lógica: la placa y el código quedan libres.
//
// **Con órdenes no se elimina; se desactiva** (#147), como un cliente con
// historial (#177): eliminar es para el activo registrado por error. Entonces la
// ventana cambia a «No se puede eliminar», dice cuántas órdenes tiene y, si sigue
// activo, ofrece desactivarlo. Cualquier otro rechazo se muestra adentro y la
// ventana sigue abierta.

interface Props {
  /** Lo que dice el texto. Sirve la fila del listado y la ficha de la edición. */
  activo: { id: unknown; code?: string | null; identifier?: string | null } | null;
  onCerrar: () => void;
  /** Después de eliminarlo. La edición vuelve al listado. */
  onEliminado?: () => void;
  /** Después de desactivarlo en lugar de eliminarlo. La edición también sale. */
  onDesactivado?: () => void;
}

/** Por qué no se pudo: tiene órdenes, u otro problema ya traducido. */
type Rechazo = { tipo: 'historial'; ordenes: number; activo: boolean } | { tipo: 'otro'; texto: string };

export function EliminarActivo({ activo, onCerrar, onEliminado, onDesactivado }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosActivos();
  const [ocupado, setOcupado] = useState(false);
  const [rechazo, setRechazo] = useState<Rechazo | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const nombre = activo ? [activo.code, activo.identifier].filter(Boolean).join(' · ') : '';
  const sinRespuesta = t('ui.formulario.aviso.sinRespuesta');

  function cerrar() {
    if (ocupado) return;
    setRechazo(null);
    setFallo(null);
    onCerrar();
  }

  async function eliminar() {
    if (!activo) return;
    setOcupado(true);
    setRechazo(null);
    try {
      const error = (await cambios.eliminar(String(activo.id))) as ProblemaBajaActivo | undefined;
      if (error) {
        setRechazo(
          error.code === 'asset.tiene_historial'
            ? { tipo: 'historial', ordenes: error.orderCount ?? 0, activo: error.isActive ?? true }
            : { tipo: 'otro', texto: error.code ? traducirError(error.code, t) : sinRespuesta },
        );
        return;
      }
    } catch {
      // Sin red la petición lanza en vez de responder (lo encontró la auditoría de #61).
      setRechazo({ tipo: 'otro', texto: sinRespuesta });
      return;
    } finally {
      setOcupado(false);
    }
    avisar({ titulo: t('admin.activos.aviso.eliminado', { nombre }) });
    onCerrar();
    onEliminado?.();
  }

  async function desactivar() {
    if (!activo) return;
    setOcupado(true);
    setFallo(null);
    try {
      const error = await cambios.cambiarEstado(String(activo.id), false);
      if (error) {
        setFallo(error.code ? traducirError(error.code, t) : sinRespuesta);
        return;
      }
    } catch {
      setFallo(sinRespuesta);
      return;
    } finally {
      setOcupado(false);
    }
    avisar({ titulo: t('admin.activos.aviso.desactivado', { nombre }) });
    setRechazo(null);
    onCerrar();
    onDesactivado?.();
  }

  if (rechazo?.tipo === 'historial' && activo) {
    const ordenes = t('admin.activos.eliminar.bloqueado.ordenes', { n: rechazo.ordenes });
    return (
      <Ventana
        key="rechazo"
        abierta
        titulo={t('admin.activos.eliminar.bloqueado.titulo')}
        descripcion={t(
          rechazo.activo ? 'admin.activos.eliminar.bloqueado.activo' : 'admin.activos.eliminar.bloqueado.inactivo',
          { nombre, ordenes },
        )}
        acciones={
          rechazo.activo ? (
            <>
              <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
                {t('admin.activos.eliminar.entendido')}
              </Boton>
              <Boton disabled={ocupado} aria-busy={ocupado} onClick={() => void desactivar()}>
                {t('admin.activos.eliminar.desactivar')}
              </Boton>
            </>
          ) : (
            <Boton onClick={cerrar}>{t('admin.activos.eliminar.entendido')}</Boton>
          )
        }
      >
        {fallo && <AvisoError aviso={{ titulo: t('admin.activos.error.titulo'), texto: fallo }} />}
      </Ventana>
    );
  }

  return (
    <Ventana
      key="confirmar"
      abierta={activo !== null}
      titulo={t('admin.activos.eliminar.titulo')}
      descripcion={activo && t('admin.activos.eliminar.texto', { nombre })}
      acciones={
        <>
          <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={ocupado} aria-busy={ocupado} onClick={() => void eliminar()}>
            {t('admin.activos.eliminar.boton')}
          </Boton>
        </>
      }
    >
      {rechazo?.tipo === 'otro' && (
        <AvisoError aviso={{ titulo: t('admin.activos.error.titulo'), texto: rechazo.texto }} />
      )}
    </Ventana>
  );
}
